import { useMemo } from 'react';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import { Link } from 'react-router-dom';
import EPT from 'edrolo/propTypes';
import { QUESTION_GROKKED } from 'edrolo/domains/exams';
import { StudentResponseBars } from '../StudentResponseBars';
import { ResultsTable } from '../../../components/ResultsTable';
import { IndicatorCell, INDICATOR_TYPES } from '../../../../../components';
import { getProfileQuestionResponseSummary } from '../../../utils';
import style from './WorkedQuestionReview.scss';

const WorkedQuestionReview = ({
  question,
  solution,
  responses,
  classStudentProfiles,
  highlightProfileId,
  getStudentCourseProgressUrl,
}) => {
  const profileQuestionResponseSummary = useMemo(
    () =>
      getProfileQuestionResponseSummary(
        classStudentProfiles,
        responses,
        solution
      ),
    [classStudentProfiles, responses, solution]
  );

  const totalMarks = (+question?.get('marks')).toFixed(0);

  const resultsTableColumns = [
    {
      id: 'name',
      Header: 'Name',
      accessor: (row) => (
        <Link
          to={getStudentCourseProgressUrl(row.get('userProfileId'))}
        >{`${row.get('userFirstName')} ${row.get('userLastName')}`}</Link>
      ),
      sortType: (rowA, rowB) =>
        rowA.original.get('userLastName').toLowerCase() >
        rowB.original.get('userLastName').toLowerCase()
          ? 1
          : -1,
    },
    {
      id: 'studentMark',
      Header: 'Student Mark',
      accessor: (row) => {
        const studentMarks = (+row.getIn([
          'response',
          'text_marks_from_student',
        ])).toFixed(0);
        if (studentMarks && !isNaN(studentMarks)) {
          return `${studentMarks}/${totalMarks}`;
        }

        return 'N/A';
      },
      sortType: (rowA, rowB) =>
        rowA.original.getIn(['response', 'marks_final']) >
        rowB.original.getIn(['response', 'marks_final'])
          ? 1
          : -1,
    },
    {
      id: 'selfReflection',
      Header: 'Self-reflection',
      accessor: (row) => {
        if (row.get('grokked') === QUESTION_GROKKED.YES) {
          return 'Understands';
        }
        if (row.get('grokked') === QUESTION_GROKKED.NO) {
          return 'Needs help';
        }
        return 'Not reflected';
      },
      Cell: (cell) => {
        if (cell.row.original.get('grokked') === QUESTION_GROKKED.YES) {
          return (
            <IndicatorCell
              value={cell.value}
              indicatorType={INDICATOR_TYPES.POSITIVE}
            />
          );
        }
        if (cell.row.original.get('grokked') === QUESTION_GROKKED.NO) {
          return (
            <IndicatorCell
              value={cell.value}
              indicatorType={INDICATOR_TYPES.NEGATIVE}
            />
          );
        }
        return (
          <IndicatorCell
            value={cell.value}
            indicatorType={INDICATOR_TYPES.NEUTRAL}
          />
        );
      },
      sortType: (rowA, rowB) => {
        const sortOrder = [
          QUESTION_GROKKED.YES,
          QUESTION_GROKKED.NO,
          QUESTION_GROKKED.NO_RESPONSE,
          null,
        ];

        if (
          sortOrder.indexOf(rowA.original.get('grokked')) >
          sortOrder.indexOf(rowB.original.get('grokked'))
        ) {
          return 1;
        }
        return -1;
      },
    },

    { id: 'rightSpacer', style: { width: '15%' } },
  ];

  return (
    <div className={style.container}>
      <div>
        <h3 className={style.heading}>How students responded</h3>
        <StudentResponseBars
          profileQuestionResponseSummary={profileQuestionResponseSummary}
          showCorrectness={false}
        />
      </div>
      <div className={style.tableContainer}>
        <h3 className={style.heading}>Response details</h3>
        <ResultsTable
          data={profileQuestionResponseSummary}
          columns={resultsTableColumns}
          highlightProfileId={highlightProfileId}
        />
      </div>
    </div>
  );
};

WorkedQuestionReview.propTypes = {
  question: EPT.ExamQuestion.isRequired,
  solution: IPT.contains({
    solution: IPT.contains({
      exemplar_response_checklist_items: IPT.listOf(
        IPT.contains({
          id: PT.number.isRequired,
          description: PT.string.isRequired,
        })
      ),
    }),
  }).isRequired,
  responses: IPT.listOf(EPT.ExamResponse),
  classStudentProfiles: IPT.orderedMapOf(
    IPT.contains({
      user_id: PT.number.isRequired,
      profile_id: PT.number.isRequired,
      first_name: PT.string.isRequired,
      last_name: PT.string.isRequired,
      display_name: PT.string,
      is_active: PT.bool,
    })
  ),
  highlightProfileId: PT.number,
  getStudentCourseProgressUrl: PT.func,
};

export default WorkedQuestionReview;
